import ReactMarkdown, { type Components } from "react-markdown";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";

import styles from "./Markdown.module.css";

// Renders agent output as Markdown (headers, bold, lists, tables via GFM)
// instead of raw text. No rehype-raw, so model output cannot inject HTML -
// react-markdown escapes anything that isn't Markdown.
//
// `remarkBreaks` makes a single newline a line break, which is what both the
// agents and the operators reading them assume. CommonMark's default is to fold
// a single newline into a space, and the agents write documents where a newline
// is load-bearing - the timeline section's own guidance is "one line per event"
// - so a nine-line dated timeline rendered as one run-on paragraph, which is
// unreadable and looks like the agent wrote it that way. Same convention as a
// GitHub comment, which is the rendering everyone here already has in mind.
// `inline` renders a short field (a table cell, a list line) without the block wrapper
// and paragraph margins, so a citation in it sits in the line like any other word. It
// keeps only inline elements, since it may sit inside a <p>, <strong> or <summary>;
// paragraphs run on with a space between them.
const INLINE_PARAGRAPH: Components = { p: ({ children }) => <>{children} </> };
const INLINE_ELEMENTS = ["p", "a", "strong", "em", "code", "del", "br"];

export const Markdown = ({
  children,
  components,
  inline = false,
}: {
  children: string;
  // Element overrides, for a caller that turns some links into its own controls.
  components?: Components;
  inline?: boolean;
}) =>
  inline ? (
    <span>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        components={{ ...components, ...INLINE_PARAGRAPH }}
        allowedElements={INLINE_ELEMENTS}
        unwrapDisallowed
      >
        {children}
      </ReactMarkdown>
    </span>
  ) : (
    <div className={styles.md}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        components={components}
      >
        {children}
      </ReactMarkdown>
    </div>
  );

export default Markdown;
